import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

export interface Todo {
  id: string;
  list_id: string;
  title: string;
  notes: string;
  due_at: string | null;
  done_at: string | null;
  starred: boolean;
  position: number;
  /** "" = the operator's own. An agent template means it is the agent's. */
  assignee: string;
  /** When the agent was told about it; null until the watcher hands it over. */
  handed_at: string | null;
  /** The task it was given as (Tasks on, assigned to an agent); null when it never became one. */
  task_id?: string | null;
  source: "manual" | "agent" | "meeting";
  source_ref: string | null;
  /** "" when the operator typed it; an agent template when an agent added it. */
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface TodoList {
  id: string;
  name: string;
  emoji: string;
  position: number;
  open_count: number;
}

export interface TodosResponse {
  lists: TodoList[];
  todos: Todo[];
}

export type TodoView = "all" | "today" | "done";

const base = (customerId: string) => `/api/customers/${customerId}/todos`;
const key = (customerId: string | null, view: TodoView, listId: string | null) =>
  ["todos", customerId, view, listId] as const;

export function useTodos(
  customerId: string | null,
  view: TodoView,
  listId: string | null,
  poll?: { refetchInterval?: number },
) {
  return useQuery({
    queryKey: key(customerId, view, listId),
    enabled: !!customerId,
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ view });
      if (listId) params.set("list_id", listId);
      return apiFetch<TodosResponse>(`${base(customerId!)}?${params}`, { signal });
    },
    // An agent can add to this list from a chat turn, so it must not look
    // stale while the tab is open. Cheap query, small payload.
    staleTime: 2_000,
    refetchInterval: poll?.refetchInterval ?? 5_000,
  });
}

/** Every mutation invalidates the whole `todos` tree: a change to one item
 *  can move it between views (done leaves All, starred joins Today) and
 *  changes a list's open count. `meta` rides on the mutation (e.g.
 *  `errorHandled`: the screen says what failed itself). */
function useTodoMutation<TArgs>(fn: (args: TArgs) => Promise<unknown>, meta?: Record<string, unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    meta,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["todos"] }),
  });
}

/** The add bar shows its own error line and puts the text back; ticks and
 *  stars keep the app's own error handling. */
export function useAddTodo() {
  return useTodoMutation(
    (a: {
      customerId: string;
      title: string;
      list_id?: string | null;
      due_at?: string | null;
      notes?: string;
      starred?: boolean;
      assignee?: string;
    }) =>
      apiFetch<Todo>(base(a.customerId), {
        method: "POST",
        body: {
          title: a.title,
          list_id: a.list_id ?? null,
          due_at: a.due_at ?? null,
          notes: a.notes ?? "",
          starred: a.starred ?? false,
          assignee: a.assignee ?? "",
        },
      }),
    { errorHandled: true },
  );
}

export function usePatchTodo() {
  return useTodoMutation(
    (a: { customerId: string; id: string; patch: Partial<Todo> & { done?: boolean } }) =>
      apiFetch<Todo>(`${base(a.customerId)}/${a.id}`, {
        method: "PATCH",
        body: a.patch,
      }),
  );
}

export function useDeleteTodo() {
  return useTodoMutation((a: { customerId: string; id: string }) =>
    apiFetch(`${base(a.customerId)}/${a.id}`, { method: "DELETE" }),
  );
}

export function useClearDone() {
  return useTodoMutation((a: { customerId: string; listId?: string | null }) =>
    apiFetch(
      `${base(a.customerId)}/clear-done${a.listId ? `?list_id=${a.listId}` : ""}`,
      { method: "POST" },
    ),
  );
}

export function useAddList() {
  return useTodoMutation((a: { customerId: string; name: string; emoji?: string }) =>
    apiFetch<TodoList>(`${base(a.customerId)}/lists`, {
      method: "POST",
      body: { name: a.name, emoji: a.emoji ?? "" },
    }),
  );
}

export function useRenameList() {
  return useTodoMutation(
    (a: { customerId: string; id: string; name?: string; emoji?: string }) =>
      apiFetch<TodoList>(`${base(a.customerId)}/lists/${a.id}`, {
        method: "PATCH",
        body: { name: a.name, emoji: a.emoji },
      }),
  );
}

export function useDeleteList() {
  return useTodoMutation((a: { customerId: string; id: string }) =>
    apiFetch(`${base(a.customerId)}/lists/${a.id}`, { method: "DELETE" }),
  );
}
